import {
  buildInquiryMailto,
  buildInquiryShareText,
  type OwnerInquiry,
} from "@/domain/owner-inquiry";
import { dataUrlToFile } from "@/lib/inquiry-images";

export type InquiryDeliveryResult = "shared" | "mailto" | "local";

/**
 * Try to get the inquiry to Coco:
 * 1) Web Share with image files (phones)
 * 2) mailto (text; images stay in the local queue)
 * 3) local queue only
 */
export async function deliverInquiryToCoco(input: {
  inquiry: OwnerInquiry;
  email: string;
}): Promise<InquiryDeliveryResult> {
  const text = buildInquiryShareText({
    topic: input.inquiry.topic,
    body: input.inquiry.body,
    contact: input.inquiry.contact,
    imageCount: input.inquiry.images.length,
    assignee: input.inquiry.assignee,
  });

  try {
    if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
      const files: File[] = [];
      for (const img of input.inquiry.images) {
        files.push(await dataUrlToFile(img.dataUrl, img.name));
      }
      const payload: ShareData = {
        title: `DSS → ${input.inquiry.assignee}`,
        text,
      };
      if (files.length > 0) {
        payload.files = files;
      }
      const can =
        typeof navigator.canShare !== "function" || navigator.canShare(payload);
      if (can) {
        await navigator.share(payload);
        return "shared";
      }
    }
  } catch (e) {
    // User cancel or unsupported — fall through.
    if (e instanceof DOMException && e.name === "AbortError") {
      return "local";
    }
  }

  const to = input.email.trim();
  if (to) {
    window.location.href = buildInquiryMailto({
      to,
      topic: input.inquiry.topic,
      body: input.inquiry.body,
      contact: input.inquiry.contact,
      imageCount: input.inquiry.images.length,
      assignee: input.inquiry.assignee,
    });
    return "mailto";
  }

  return "local";
}

export function deliveryNoticeJa(
  result: InquiryDeliveryResult,
  opts?: { wantedDeliver?: boolean },
): string {
  switch (result) {
    case "shared":
      return "共有シートを開きました。送る相手と内容はご自身で確認してください。業務データ全体は相手に渡りません。";
    case "mailto":
      return "メール下書きを開きました。本文・添付した画像だけが対象です。業務データ全体は端末内のままです。";
    default:
      if (opts?.wantedDeliver) {
        return "外への送信ができませんでした（共有未対応、またはメール宛先未設定）。内容はこの端末の改修メモに残してあります。開発者・運営者は自動では見られません。";
      }
      return "この端末の改修メモ（非公開）に残しました。開発者・運営者は中身を見られません。外へ出すときは「共有／送る」を使ってください。";
  }
}
